import { useState, useEffect } from 'react';
import { Save, AlertCircle, Loader2, Info, ArrowUp, ArrowDown, GripVertical, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../api';

export default function Allocations() {
  const [incomes, setIncomes] = useState([]);
  const [selectedIncomeId, setSelectedIncomeId] = useState('');
  const [expectedPaycheck, setExpectedPaycheck] = useState(0);
  
  const [funds, setFunds] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [recentExecutions, setRecentExecutions] = useState([]);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isFetchingExecutions, setIsFetchingExecutions] = useState(false);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [confirmSmartAllocate, setConfirmSmartAllocate] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [draggedItemIndex, setDraggedItemIndex] = useState(null);
  const [dragOverItemIndex, setDragOverItemIndex] = useState(null);

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (selectedIncomeId) {
      const inc = incomes.find(i => i.id === selectedIncomeId);
      if (inc) setExpectedPaycheck(inc.amount);
      fetchAllocations(selectedIncomeId);
      fetchRecentExecutions(selectedIncomeId);
    } else {
      setAllocations([]);
      setExpectedPaycheck(0);
      setRecentExecutions([]);
    }
  }, [selectedIncomeId]);



  const fetchInitialData = async () => {
    try {
      const [fundsRes, incomesRes] = await Promise.all([
        api.get('/funds'),
        api.get('/incomes')
      ]);
      setFunds(fundsRes.data);
      setIncomes(incomesRes.data);
      
      if (incomesRes.data.length > 0) {
        setSelectedIncomeId(incomesRes.data[0].id);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllocations = async (incomeId) => {
    setLoading(true);
    try {
      const res = await api.get('/allocations');
      // Filter the global allocations list to just the ones for this income source
      const filtered = res.data.filter(a => a.income_id === incomeId);
      
      setAllocations(filtered.map(a => ({
        id: a.id,
        fund_id: a.fund_id,
        name: a.fund_name,
        type: a.type,
        amount: Math.round(parseFloat(a.amount) * 100) / 100,
        due_day: a.due_day,
        rollover_fund_id: a.rollover_fund_id,
        color: a.color
      })));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentExecutions = async (incomeId) => {
    if (!incomeId) return;
    setIsFetchingExecutions(true);
    try {
      const res = await api.get(`/allocations/executions/${incomeId}`);
      setRecentExecutions(res.data.executions || []);
    } catch (err) {
      console.error('Execution fetch error', err);
    } finally {
      setIsFetchingExecutions(false);
    }
  };

  const handleSave = async () => {
    if (!selectedIncomeId) return;
    setSaving(true);
    try {
      await api.post('/allocations/save-rules', {
        incomeId: selectedIncomeId,
        rules: allocations
      });
      setHasUnsavedChanges(false);
      toast.success('Allocation rules saved successfully!');
    } catch (err) {
      toast.error('Error saving rules');
    } finally {
      setSaving(false);
    }
  };

  const updateAllocation = (id, field, value) => {
    setAllocations(allocations.map(a => a.id === id ? { ...a, [field]: value } : a));
    setHasUnsavedChanges(true);
  };

  const moveAllocation = (index, direction) => {
    const newAllocations = [...allocations];
    if (direction === 'up' && index > 0) {
      const temp = newAllocations[index - 1];
      newAllocations[index - 1] = newAllocations[index];
      newAllocations[index] = temp;
      setAllocations(newAllocations);
      setHasUnsavedChanges(true);
    } else if (direction === 'down' && index < newAllocations.length - 1) {
      const temp = newAllocations[index + 1];
      newAllocations[index + 1] = newAllocations[index];
      newAllocations[index] = temp;
      setAllocations(newAllocations);
      setHasUnsavedChanges(true);
    }
  };

  const handleDrop = (dropIndex) => {
    if (draggedItemIndex === null || draggedItemIndex === dropIndex) return;
    const newAllocations = [...allocations];
    const draggedItem = newAllocations.splice(draggedItemIndex, 1)[0];
    newAllocations.splice(dropIndex, 0, draggedItem);
    setAllocations(newAllocations);
    setHasUnsavedChanges(true);
    setDraggedItemIndex(null);
    setDragOverItemIndex(null);
  };

  const addAllocationRule = async (fundId) => {
    const fund = funds.find(f => f.id === fundId);
    if (!fund) return;

    const newAllocations = [...allocations, {
      id: Date.now().toString(),
      fund_id: fund.id,
      name: fund.name,
      type: 'fixed',
      amount: 0,
      due_day: '',
      rollover_fund_id: '',
      color: fund.color
    }];
    setAllocations(newAllocations);
    setHasUnsavedChanges(true);
  };

  const handleGlobalSmartAllocate = async () => {
    if (!confirmSmartAllocate) {
      setConfirmSmartAllocate(true);
      return;
    }
    
    setIsOptimizing(true);
    try {
      await api.post('/allocations/global-smart-allocate');
      fetchAllocations(selectedIncomeId);
      toast.success('Success! Your entire paycheck rules have been automatically optimized based on your bucket targets.');
      setConfirmSmartAllocate(false);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || 'Failed to generate global smart allocations');
    } finally {
      setIsOptimizing(false);
    }
  };

  const removeAllocation = async (idToRemove) => {
    const newAllocations = allocations.filter(a => a.id !== idToRemove);
    setAllocations(newAllocations);
    setHasUnsavedChanges(true);
  };

  const liveUnallocated = allocations.reduce((rem, a) => {
    if (a.type === 'fixed') return rem - (parseFloat(a.amount) || 0);
    if (a.type === 'percentage') return rem - (expectedPaycheck * ((parseFloat(a.amount) || 0) / 100));
    return rem;
  }, expectedPaycheck);

  if (loading && incomes.length === 0) {
    return <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}><Loader2 className="text-muted" style={{ animation: 'spin 2s linear infinite' }} /></div>;
  }

  if (incomes.length === 0) {
    return (
      <div className="glass" style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>
        <h3>No Income Sources Found</h3>
        <p>Please go to Settings to define your paychecks before setting up allocations.</p>
      </div>
    );
  }

  return (
    <div className="animate-fade-in responsive-grid" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 'var(--spacing-md)' }}>
      
      {/* Allocation Builder */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>
        
        {/* Income Selector */}
        <div className="glass mobile-flex-col" style={{ padding: 'var(--spacing-lg)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
          <div style={{ width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
              <h2 style={{ margin: 0 }}>Paycheck Rules</h2>
            </div>
          </div>
          
          <div className="mobile-flex-col" style={{ display: 'flex', alignItems: 'center', gap: '1rem', width: '100%' }}>
            <label style={{ color: 'var(--text-muted)' }}>Select Income:</label>
            <select 
              value={selectedIncomeId} 
              onChange={(e) => setSelectedIncomeId(e.target.value)}
              style={{ minWidth: '200px', fontSize: '1rem', padding: '0.6rem' }}
            >
              {incomes.map(inc => (
                <option key={inc.id} value={inc.id}>{inc.nickname || inc.name} (${inc.amount.toLocaleString()})</option>
              ))}
            </select>
          </div>
        </div>

        {/* Advanced Mode Banner */}
        <div 
          className="glass mobile-flex-col" 
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between', 
            gap: '1.5rem', 
            padding: '1.25rem 1.5rem',
            marginBottom: 'var(--spacing-md)',
            borderLeft: '4px solid var(--warning)'
          }}
        >
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flex: 1 }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '38px',
              height: '38px',
              borderRadius: 'var(--radius-sm)',
              background: 'rgba(245, 158, 11, 0.08)',
              border: '1px solid rgba(245, 158, 11, 0.15)',
              color: 'var(--warning)',
              flexShrink: 0
            }}>
              <AlertCircle size={20} />
            </div>
            <div>
              <h4 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1.05rem', color: 'var(--text-main)', fontWeight: 600 }}>
                Manual Routing Mode
                <span style={{
                  fontSize: '0.65rem',
                  textTransform: 'uppercase',
                  fontWeight: 700,
                  letterSpacing: '0.05em',
                  background: 'rgba(245, 158, 11, 0.12)',
                  color: 'var(--warning)',
                  padding: '2px 8px',
                  borderRadius: '12px',
                  border: '1px solid rgba(245, 158, 11, 0.15)'
                }}>
                  Active
                </span>
              </h4>
              <p style={{ margin: '0.35rem 0 0 0', color: 'var(--text-muted)', fontSize: '0.875rem', lineHeight: '1.5' }}>
                Rules execute strictly top-to-bottom. If things get complicated or your rules break, you can instantly reset everything to perfect defaults using the Auto-Setup.
              </p>
            </div>
          </div>

          {incomes.length > 0 && funds.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem', flexShrink: 0 }}>
              <button 
                className="btn transform hover:-translate-y-0.5 active:translate-y-0 hover:scale-[1.02] transition-all duration-300" 
                onClick={handleGlobalSmartAllocate} 
                disabled={isOptimizing} 
                style={{ 
                  padding: '0.55rem 1.1rem', 
                  background: confirmSmartAllocate 
                    ? 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)' 
                    : 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)', 
                  border: 'none', 
                  boxShadow: confirmSmartAllocate 
                    ? '0 4px 12px rgba(245, 158, 11, 0.2)' 
                    : '0 4px 12px rgba(79, 70, 230, 0.2)',
                  color: 'white',
                  fontWeight: 500,
                  fontSize: '0.9rem',
                  borderRadius: 'var(--radius-sm)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  cursor: 'pointer'
                }}
              >
                {isOptimizing ? <Loader2 size={16} className="spin" /> : <Sparkles size={16} className={confirmSmartAllocate ? 'animate-bounce' : ''} />} 
                {confirmSmartAllocate ? 'Confirm Overwrite?' : 'Re-run Auto-Setup'}
              </button>
              <span className="text-muted" style={{ fontSize: '0.75rem', textAlign: 'right', display: 'block', width: '100%' }}>This will overwrite your manual rules.</span>
            </div>
          )}
        </div>

        <div className="glass" style={{ padding: 'var(--spacing-lg)', minHeight: '400px' }}>
          
          <div className="mobile-flex-col" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', paddingBottom: '1rem', borderBottom: '1px solid var(--surface-border)', gap: '1rem' }}>
            <div style={{ width: '100%' }}>
              <span className="text-muted" style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>Selected Paycheck Amount <span title="The expected deposit amount from Settings" style={{ cursor: 'help' }}>ⓘ</span></span>
              <h3 style={{ margin: 0, fontSize: '1.5rem', color: 'var(--primary)' }}>${expectedPaycheck.toLocaleString(undefined, {minimumFractionDigits: 2})}</h3>
            </div>
            <div style={{ textAlign: 'left', width: '100%' }}>
              <span className="text-muted" style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>Unallocated (Safe-to-Spend) <span title="Money left over after all rules execute" style={{ cursor: 'help' }}>ⓘ</span></span>
              <h3 style={{ margin: 0, fontSize: '1.5rem', color: liveUnallocated < 0 ? 'var(--danger)' : 'var(--success)' }}>
                ${liveUnallocated.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
              </h3>
            </div>
          </div>

          <div style={{ marginBottom: '1.5rem', display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap', justifyContent: 'space-between' }}>
            <div style={{ flex: 1, minWidth: '250px' }}>
              <label style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>Add Rule for Fund</label>
              {funds.length === 0 ? (
                <div style={{ padding: '0.8rem 1rem', background: 'rgba(255,255,255,0.05)', borderRadius: 'var(--radius-sm)', color: 'var(--text-muted)', fontSize: '0.9rem', display: 'inline-block' }}>
                  You have no funds created yet! Head over to the <Link to="/funds" style={{ color: 'var(--primary)', textDecoration: 'none', fontWeight: 600 }}>Funds</Link> tab to create your first bucket.
                </div>
              ) : (
                <select onChange={(e) => addAllocationRule(e.target.value)} value="" style={{ width: '100%', maxWidth: '300px' }}>
                  <option value="" disabled>Select a fund...</option>
                  {funds.map(f => (
                    <option key={f.id} value={f.id}>{f.name}</option>
                  ))}
                </select>
              )}
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', alignItems: 'center', background: 'rgba(0,0,0,0.2)', padding: '0.75rem', borderRadius: 'var(--radius-md)' }}>
              {hasUnsavedChanges && <span style={{ display: 'flex', alignItems: 'center', color: 'var(--warning)', fontSize: '0.85rem', fontWeight: 500 }}>Unsaved changes!</span>}
              <button className="btn btn-glass" onClick={() => fetchAllocations(selectedIncomeId)}>Discard</button>
              <button className={`btn ${hasUnsavedChanges ? 'btn-primary' : 'btn-glass'}`} onClick={handleSave} disabled={saving} style={{ transition: 'all 0.2s', boxShadow: hasUnsavedChanges ? '0 0 15px rgba(59, 130, 246, 0.4)' : 'none' }}>
                {saving ? <Loader2 size={18} style={{ animation: 'spin 2s linear infinite' }} /> : <Save size={18} />} 
                {hasUnsavedChanges ? 'Save & Preview Rules' : 'Saved'}
              </button>
            </div>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}><Loader2 className="text-muted" style={{ animation: 'spin 2s linear infinite' }} /></div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {allocations.map((alloc, idx) => (
                <div 
                  key={alloc.id} 
                  draggable
                  onDragStart={(e) => {
                    setDraggedItemIndex(idx);
                    e.dataTransfer.effectAllowed = 'move';
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'move';
                    if (dragOverItemIndex !== idx) setDragOverItemIndex(idx);
                  }}
                  onDragLeave={() => {
                    if (dragOverItemIndex === idx) setDragOverItemIndex(null);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    handleDrop(idx);
                  }}
                  onDragEnd={() => {
                    setDraggedItemIndex(null);
                    setDragOverItemIndex(null);
                  }}
                  style={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    gap: '1rem', 
                    background: draggedItemIndex === idx ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.02)', 
                    padding: '1.25rem', 
                    borderRadius: 'var(--radius-lg)', 
                    border: dragOverItemIndex === idx ? '1px dashed var(--primary)' : '1px solid var(--surface-border)',
                    opacity: draggedItemIndex === idx ? 0.5 : 1,
                    transition: 'all 0.2s ease',
                  }}
                >
                  
                  {/* Reorder Controls */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '2px', alignItems: 'center', cursor: 'grab' }}>
                    <GripVertical size={20} style={{ opacity: 0.5, marginBottom: '4px' }} />
                    <button className="btn btn-glass" style={{ padding: '2px', border: 'none', background: idx === 0 ? 'transparent' : 'rgba(255,255,255,0.05)', color: idx === 0 ? 'transparent' : 'inherit', cursor: idx === 0 ? 'default' : 'pointer' }} onClick={() => moveAllocation(idx, 'up')} disabled={idx === 0}><ArrowUp size={14}/></button>
                    <button className="btn btn-glass" style={{ padding: '2px', border: 'none', background: idx === allocations.length - 1 ? 'transparent' : 'rgba(255,255,255,0.05)', color: idx === allocations.length - 1 ? 'transparent' : 'inherit', cursor: idx === allocations.length - 1 ? 'default' : 'pointer' }} onClick={() => moveAllocation(idx, 'down')} disabled={idx === allocations.length - 1}><ArrowDown size={14}/></button>
                  </div>

                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    
                    {/* Header: Name and Delete */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <div style={{ width: '14px', height: '14px', borderRadius: '50%', backgroundColor: alloc.color }}></div>
                        <span style={{ fontWeight: 600, fontSize: '1.1rem', letterSpacing: '0.02em' }}>{alloc.name}</span>
                      </div>
                      <button 
                        className="btn btn-glass" 
                        style={{ padding: '0.4rem', color: 'var(--danger)', border: 'none', background: 'transparent' }}
                        onClick={() => removeAllocation(alloc.id)}
                        title="Remove Rule"
                      >
                        <AlertCircle size={18} />
                      </button>
                    </div>
                    
                    {/* Controls Grid */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', alignItems: 'flex-start' }}>
                      
                      {/* Type & Amount */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                        <label style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontWeight: 500 }}>Rule Type</label>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <select 
                            value={alloc.type} 
                            onChange={(e) => updateAllocation(alloc.id, 'type', e.target.value)}
                            style={{ flex: 1, padding: '0.6rem' }}
                          >
                            <option value="fixed">Fixed ($)</option>
                            <option value="percentage">Percent (%)</option>
                            <option value="remainder">Remainder</option>
                          </select>
                          
                          {alloc.type !== 'remainder' && (
                            <div style={{ position: 'relative', width: '150px', flexShrink: 0 }}>
                              <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }}>
                                {alloc.type === 'fixed' ? '$' : '%'}
                              </span>
                              <input 
                                type="number" 
                                step="0.01"
                                value={alloc.amount}
                                onChange={(e) => updateAllocation(alloc.id, 'amount', e.target.value)}
                                style={{ paddingLeft: '28px', width: '100%', height: '100%' }}
                              />
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Advanced Options (Spillover & Due Day) */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                        <label style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontWeight: 500 }}>Leftover Money</label>
                        {alloc.type !== 'remainder' ? (
                          <select 
                            value={alloc.rollover_fund_id || ''} 
                            onChange={(e) => updateAllocation(alloc.id, 'rollover_fund_id', e.target.value)}
                            style={{ width: '100%', padding: '0.6rem' }}
                          >
                            <option value="">Keep Unallocated</option>
                            {funds.filter(f => f.id !== alloc.fund_id).map(f => (
                              <option key={f.id} value={f.id}>Spill into {f.name}</option>
                            ))}
                          </select>
                        ) : (
                          <div style={{ padding: '0.6rem', color: 'var(--text-muted)', fontSize: '0.9rem', background: 'rgba(255,255,255,0.02)', borderRadius: 'var(--radius-sm)', border: '1px dashed rgba(255,255,255,0.1)' }}>
                            Catches all unallocated funds.
                          </div>
                        )}
                      </div>

                      {alloc.type === 'fixed' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                          <label style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontWeight: 500 }}>Due Day</label>
                          <input
                            type="number"
                            min="1"
                            max="31"
                            placeholder="Optional (1-31)"
                            value={alloc.due_day || ''}
                            onChange={(e) => {
                              const val = e.target.value.replace(/\D/g, '');
                              if (val === '') {
                                updateAllocation(alloc.id, 'due_day', '');
                                return;
                              }
                              const num = parseInt(val, 10);
                              if (num > 31) updateAllocation(alloc.id, 'due_day', '31');
                              else if (num < 1) updateAllocation(alloc.id, 'due_day', '1');
                              else updateAllocation(alloc.id, 'due_day', num.toString());
                            }}
                            style={{ width: '120px', padding: '0.6rem' }}
                          />
                        </div>
                      )}
                      
                    </div>
                  </div>
                </div>
              ))}
              
              {allocations.length === 0 && (
                <div style={{ textAlign: 'center', padding: '3rem 2rem', color: 'var(--text-muted)', background: 'rgba(255,255,255,0.02)', borderRadius: 'var(--radius-lg)', border: '1px dashed rgba(255,255,255,0.1)' }}>
                  <Info size={32} style={{ opacity: 0.5, marginBottom: '1rem' }} />
                  <p style={{ margin: 0 }}>No rules set for this income source yet.</p>
                  <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem', opacity: 0.7 }}>Add your first fund routing rule using the dropdown above.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Recent Executions Ledger */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>
        <div className="glass" style={{ padding: 'var(--spacing-lg)' }}>
          <h4 style={{ margin: '0 0 1.5rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            Recent Executions (Real History)
          </h4>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', fontSize: '0.95rem' }}>
            {isFetchingExecutions ? (
              <div style={{ textAlign: 'center', padding: '3rem 0' }}><Loader2 className="spin text-muted" /></div>
            ) : recentExecutions.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '3rem 0', color: 'var(--text-muted)' }}>
                No recent transactions found for this income source. 
                <br/><br/>
                <span style={{ fontSize: '0.85rem' }}>Once a paycheck arrives and is routed by the Autopilot, the exact step-by-step history will appear here.</span>
              </div>
            ) : (
              recentExecutions.map((exec, idx) => (
                <div key={exec.transactionId} style={{ background: 'rgba(255,255,255,0.02)', padding: '1rem', borderRadius: 'var(--radius-md)', border: '1px solid rgba(255,255,255,0.05)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '0.75rem', borderBottom: '1px solid rgba(255,255,255,0.1)', marginBottom: '0.75rem' }}>
                    <div>
                      <span style={{ color: 'var(--text-muted)', display: 'block', fontSize: '0.8rem' }}>Paycheck Detected</span>
                      <strong style={{ fontSize: '1.1rem' }}>{new Date(exec.date).toLocaleDateString()}</strong>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <span style={{ color: 'var(--text-muted)', display: 'block', fontSize: '0.8rem' }}>Total Deposited</span>
                      <strong style={{ fontSize: '1.1rem', color: 'var(--success)' }}>+${exec.totalDeposit.toFixed(2)}</strong>
                    </div>
                  </div>
                  
                  <div style={{ color: 'var(--primary)', margin: '0.5rem 0 0.5rem', fontSize: '0.85rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Transfer Steps</div>
                  
                  {exec.entries.map((entry, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.4rem 0' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: entry.color || 'var(--text-muted)' }}></div>
                        {entry.fundName || 'Unallocated (Safe-to-Spend)'}
                      </span>
                      <span style={{ fontFamily: 'monospace', fontSize: '1rem', color: entry.fundName ? 'var(--text-main)' : 'var(--success)' }}>
                        {entry.fundName ? '-' : ''}${(entry.amount || 0).toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
              ))
            )}
          </div>
        </div>

        {liveUnallocated < 0 && (
          <div className="glass" style={{ padding: '1rem', background: 'rgba(239, 68, 68, 0.1)', borderColor: 'rgba(239, 68, 68, 0.3)', display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
            <AlertCircle color="var(--danger)" style={{ flexShrink: 0 }} />
            <div>
              <strong style={{ display: 'block', color: 'var(--danger)', marginBottom: '0.25rem' }}>Over-Allocated</strong>
              <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                Your fixed allocations exceed your expected paycheck amount. Adjust your rules to ensure a positive safe-to-spend balance.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
